import {
  UzBadge,
  UzButton,
  UzDialog,
  UzEmptyState,
  UzErrorState,
  UzInput,
  UzSpinner,
} from '@utazzo/ui';
import { Shuffle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { lpsErrorText, useAssignLpsLead, useLpsLeadAgents } from '../use-lps';

import type { LpsLeadItem } from '../lps.api';

/**
 * Route a queued lead by hand. Experts for the lead's destination are listed
 * first; anyone active can be picked. "Try round-robin again" is for after an
 * expert was mapped to the destination on the user form.
 */
export const AssignAgentDialog = ({
  lead,
  onClose,
}: {
  lead: Pick<LpsLeadItem, 'id' | 'fullName' | 'destination' | 'forward' | 'status'> | null;
  onClose: () => void;
}): React.JSX.Element => {
  const agents = useLpsLeadAgents(lead?.id ?? null);
  const assign = useAssignLpsLead();
  const [picked, setPicked] = useState<string | null>(null);

  useEffect(() => {
    setPicked(null);
  }, [lead?.id]);

  const destination = agents.data?.destination ?? null;
  const expertCount = agents.data?.agents.filter((a) => a.isExpert).length ?? 0;

  const run = async (userId: string | null) => {
    if (!lead) return;
    try {
      await assign.mutateAsync({ id: lead.id, userId });
      onClose();
    } catch {
      // Toast already shown; keep the dialog open.
    }
  };

  return (
    <UzDialog
      open={lead !== null}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title={`Assign ${lead?.fullName ?? 'this lead'}`}
      description={
        destination
          ? `Destination matched: ${destination.name}. ${expertCount} expert${expertCount === 1 ? '' : 's'} mapped.`
          : `No CRM destination matched "${lead?.destination ?? 'no destination'}". Pick an agent.`
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <UzButton
            variant="outline"
            leftIcon={<Shuffle className="size-4" />}
            disabled={assign.isPending}
            onClick={() => void run(null)}
            tooltip={
              lead?.status === 'DUPLICATE'
                ? 'Give it to the next expert in turn'
                : 'Use after mapping an expert to this destination'
            }
          >
            {lead?.status === 'DUPLICATE' ? 'Assign by round-robin' : 'Try round-robin again'}
          </UzButton>
          <div className="flex gap-2">
            <UzButton variant="outline" onClick={onClose} disabled={assign.isPending}>
              Cancel
            </UzButton>
            <UzButton
              disabled={!picked}
              loading={assign.isPending}
              onClick={() => void run(picked)}
            >
              Assign and send to CRM
            </UzButton>
          </div>
        </div>
      }
    >
      {lead?.status === 'DUPLICATE' && (
        <p className="mb-3 rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800">
          This is a repeat enquiry from someone who enquired in the last 30 days. Assigning it
          creates a new CRM lead under the same customer - use it for a new trip or another
          destination, not for the same enquiry twice.
        </p>
      )}
      {lead?.status !== 'DUPLICATE' && lead?.forward.error && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {lead.forward.error}
        </p>
      )}
      <LpsAgentPicker key={lead?.id} agents={agents} picked={picked} onPick={setPicked} />
    </UzDialog>
  );
};

/**
 * Searchable agent list for a lead, destination experts first. Shared by the
 * single assign above and the bulk assign on the grid's selection bar.
 */
export const LpsAgentPicker = ({
  agents,
  picked,
  onPick,
  onlyIds,
}: {
  agents: ReturnType<typeof useLpsLeadAgents>;
  picked: string | null;
  onPick: (userId: string) => void;
  /** Narrow the list to these users (a sales lead's own team); null = everyone. */
  onlyIds?: ReadonlySet<string> | null;
}): React.JSX.Element => {
  const [search, setSearch] = useState('');

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = (agents.data?.agents ?? []).filter((a) => !onlyIds || onlyIds.has(a.id));
    if (!q) return all;
    return all.filter(
      (a) =>
        a.fullName.toLowerCase().includes(q) ||
        a.destinations.some((d) => d.toLowerCase().includes(q)),
    );
  }, [agents.data, search, onlyIds]);

  return (
    <>
      <UzInput
        id="lps-assign-search"
        placeholder="Search agent or destination"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="mt-3 max-h-80 overflow-y-auto rounded-md border border-zinc-200">
        {agents.isLoading && (
          <div className="flex justify-center py-8">
            <UzSpinner />
          </div>
        )}
        {agents.isError && (
          <UzErrorState
            size="compact"
            title="Could not load agents"
            description={lpsErrorText(agents.error, 'Try again.')}
            onRetry={() => void agents.refetch()}
          />
        )}
        {agents.data && list.length === 0 && <UzEmptyState size="compact" title="Nobody matches" />}
        <ul role="listbox" aria-label="Agents" className="divide-y divide-zinc-100">
          {list.map((a) => {
            const selected = picked === a.id;
            return (
              <li key={a.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => onPick(a.id)}
                  className={`focus-visible:outline-primary flex w-full items-start justify-between gap-3 px-3 py-2 text-left focus-visible:outline-2 ${
                    selected ? 'bg-primary/10' : 'hover:bg-zinc-50'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-zinc-900">{a.fullName}</span>
                    <span className="block truncate text-xs text-zinc-500">
                      {a.destinations.length > 0
                        ? a.destinations.join(', ')
                        : 'No destinations mapped'}
                    </span>
                  </span>
                  {a.isExpert && <UzBadge tone="success">Expert</UzBadge>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};
